import React, { useState, useMemo } from "react";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getSecAssessmentByClientID,
  createSecAssessment,
  deleteSecAssessmentID,
} from "../../../../../../api/services/observations";
import {
  Box,
  Card,
  Typography,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { Air as RepositioningIcon } from "@mui/icons-material";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CommonButton from "../../../../../CustomForm/CommonButton";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import { RepositioningOptions } from "./repositioning.config";
import CustomSelect from "../../../../../FormFields/CustomSelect";
import EditRepositioningModal from "./editRepositioningModal";

const getNowDateTime = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const Repositioning = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({ date_time: getNowDateTime() });
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [RowID, setRowID] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const assessmentType = "repositioning";

  const profile = useUserProfile();

  const repositioningSelectOptions = useMemo(
    () =>
      RepositioningOptions.map((opt) => ({ value: opt, label: opt })),
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      const updatedFormData = {
        ...formData,
        client: clientID,
        carer: profile.id,
        assessment_type: assessmentType,
      };
      setFormData(updatedFormData);
      const response = await createSecAssessment(updatedFormData);
      if (response.status === 201) {
        enqueueSnackbar("Data has been created Successfully.", {
          variant: "success",
        });
        queryClient.invalidateQueries(QK.repositionings);
        setFormData({ date_time: getNowDateTime() });
      }
    } catch (error) {
      enqueueSnackbar("An error occurred while creating the Data.", {
        variant: "error",
      });
    }
  };

  const handleCheckBox = async (isChecked, name) => {
    setFormData((prev) => ({ ...prev, [name]: isChecked }));
  };

  const handleOpenModal = (id) => {
    setSelectedID(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedID(null);
  };

  const { data: assessments } = useQuery(
    [QK.repositionings, clientID],
    () => getSecAssessmentByClientID(clientID, assessmentType)
  );

  const deleteMutation = useMutation(deleteSecAssessmentID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.repositionings]);
      enqueueSnackbar("Data deleted successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete Data", { variant: "error" });
    },
  });

  const handleUpdate = (id) => {
    handleOpenModal(id);
    setShowModal(true);
  };

  const headers = ["type", "notes", "date_time"];

  const handleDelete = (ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  };

  const handleClose = () => setShowConfirmModal(false);

  const handleConfirm = () => {
    deleteMutation.mutate(RowID);
    handleClose();
  };

  return (
    <Box>
      <Card
        sx={{
          borderRadius: "14px",
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          border: "1px solid #EEF1F5",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            background: "linear-gradient(135deg, #FAFBFC 0%, #F4F7FB 100%)",
            borderBottom: "1px solid #EEF1F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.14
                )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <RepositioningIcon
                sx={{ color: theme.palette.primary.main, fontSize: 18 }}
              />
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: "#1A1A2E", lineHeight: 1.2 }}
              >
                Add Repositioning Entry
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#7A8699", fontSize: "0.68rem" }}
              >
                Record a new repositioning entry
              </Typography>
            </Box>
          </Box>
          <Chip
            label="New"
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: "#D1FAE5",
              color: "#039855",
              borderRadius: "6px",
            }}
          />
        </Box>

        <Box sx={{ p: 2.5 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75 }}>
              <Box sx={{ flex: "1 1 calc(50% - 9px)", minWidth: "220px" }}>
                <CustomSelect
                  options={repositioningSelectOptions}
                  label="Repositioning Type *"
                  name="type"
                  value={formData?.type || ""}
                  onChange={handleChange}
                  searchable={true}
                  searchPlaceholder="Search repositioning type..."
                />
              </Box>

              <Box sx={{ flex: "1 1 calc(50% - 9px)", minWidth: "220px" }}>
                <CustomInput
                  label="Date & Time *"
                  name="date_time"
                  type="datetime-local"
                  value={formData?.date_time || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box sx={{ flex: "1 1 100%" }}>
                <CustomTextArea
                  label="Notes"
                  placeholder="Recorded Notes"
                  name="notes"
                  value={formData?.notes || ""}
                  onChange={handleChange}
                />
              </Box>

              <Box
                sx={{
                  flex: "1 1 calc(50% - 9px)",
                  minWidth: "220px",
                  display: "flex",
                  alignItems: "center",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "10px",
                  border: "1px solid #EEF1F5",
                  bgcolor: "#FAFBFC",
                }}
              >
                <CustomCheckbox
                  label="Share with Manager"
                  name="manager"
                  checked={formData?.manager || false}
                  onClick={handleCheckBox}
                />
              </Box>

              <Box
                sx={{
                  flex: "1 1 calc(50% - 9px)",
                  minWidth: "220px",
                  display: "flex",
                  alignItems: "center",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "10px",
                  border: "1px solid #EEF1F5",
                  bgcolor: "#FAFBFC",
                }}
              >
                <CustomCheckbox
                  label="Handover"
                  name="handover"
                  checked={formData?.handover || false}
                  onClick={handleCheckBox}
                />
              </Box>

              <Box
                sx={{
                  flex: "1 1 100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  pt: 1.25,
                  mt: 0.25,
                  borderTop: "1px dashed #E5E9F0",
                }}
              >
                <CommonButton type="submit" onSubmit={handleSubmit} />
              </Box>
            </Box>
          </form>
        </Box>

        <DataTable
          data={assessments}
          headers={headers}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          canUpdate={true}
          canDelete={true}
        />

        <ConfirmModal
          show={showConfirmModal}
          handleClose={handleClose}
          handleConfirm={handleConfirm}
          title="Confirm Delete"
          body="Are you sure you want to delete this data?"
        />

        <EditRepositioningModal
          show={showModal}
          handleClose={handleCloseModal}
          selectedID={selectedID}
        />
      </Card>
    </Box>
  );
};

export default Repositioning;